import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { theme } from '../../styles/theme';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../config/api';
import toast from 'react-hot-toast';
import {
  Cog6ToothIcon,
  MapPinIcon,
  BellIcon,
  CheckIcon
} from '@heroicons/react/24/outline';

const Container = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, ${theme.colors.background.primary} 0%, ${theme.colors.background.secondary} 100%);
  padding: 2rem;
  max-width: 800px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  font-size: ${theme.fontSizes.h1};
  font-weight: ${theme.fontWeights.bold};
  color: ${theme.colors.text.primary};
  margin: 0;
`;

const Card = styled.div`
  background: ${theme.colors.background.paper};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  padding: 1.5rem;
  margin-bottom: 1.5rem;
`;

const CardTitle = styled.h2`
  font-size: ${theme.fontSizes.lg};
  font-weight: ${theme.fontWeights.semibold};
  color: ${theme.colors.text.primary};
  margin: 0 0 1rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const SettingRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 0;
  border-bottom: 1px solid ${theme.colors.border.secondary};

  &:last-child {
    border-bottom: none;
  }
`;

const SettingInfo = styled.div`
  flex: 1;
`;

const SettingLabel = styled.div`
  font-weight: ${theme.fontWeights.medium};
  color: ${theme.colors.text.primary};
  margin-bottom: 0.25rem;
`;

const SettingDescription = styled.div`
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.text.secondary};
`;

const Toggle = styled.label`
  position: relative;
  display: inline-block;
  width: 52px;
  height: 28px;
`;

const ToggleInput = styled.input`
  opacity: 0;
  width: 0;
  height: 0;

  &:checked + span {
    background-color: ${theme.colors.primary.main};
  }

  &:checked + span:before {
    transform: translateX(24px);
  }
`;

const ToggleSlider = styled.span`
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: ${theme.colors.background.tertiary};
  transition: 0.3s;
  border-radius: 28px;

  &:before {
    position: absolute;
    content: "";
    height: 20px;
    width: 20px;
    left: 4px;
    bottom: 4px;
    background-color: white;
    transition: 0.3s;
    border-radius: 50%;
  }
`;

const Input = styled.input`
  padding: 0.5rem;
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.md};
  background: ${theme.colors.background.secondary};
  color: ${theme.colors.text.primary};
  width: 100px;
  text-align: right;
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  background: ${theme.colors.primary.main};
  color: white;
  border: none;
  border-radius: ${theme.borderRadius.md};
  font-weight: ${theme.fontWeights.medium};
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &:hover {
    background: ${theme.colors.primary.dark};
  }
`;

const LocationPreferences = () => {
  const { user } = useAuth();
  const [preferences, setPreferences] = useState({
    share_with_friends: true,
    share_location: true,
    proximity_notifications: true,
    proximity_radius_nm: 5.0,
    auto_share_active_vessel: true
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadPreferences();
  }, []);

  const loadPreferences = async () => {
    try {
      const response = await api.get('/api/friends/location/preferences');
      if (response.data) {
        setPreferences(response.data);
      }
    } catch (error) {
      console.error('Error loading preferences:', error);
    } finally {
      setLoading(false);
    }
  };

  const updatePreference = (key, value) => {
    setPreferences(prev => ({ ...prev, [key]: value }));
  };

  const savePreferences = async () => {
    try {
      setSaving(true);
      await api.put('/api/friends/location/preferences', preferences);
      toast.success('Preferences saved successfully');
    } catch (error) {
      console.error('Error saving preferences:', error);
      toast.error('Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container>
        <div style={{ textAlign: 'center', padding: '4rem' }}>Loading...</div>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <Cog6ToothIcon className="w-8 h-8" />
        <Title>Location Sharing Preferences</Title>
      </Header>

      <Card>
        <CardTitle>
          <MapPinIcon className="w-5 h-5" />
          Location Sharing
        </CardTitle>
        
        <SettingRow>
          <SettingInfo>
            <SettingLabel>Share Location with Friends</SettingLabel>
            <SettingDescription>
              Allow friends to see your vessel's current location on the map
            </SettingDescription>
          </SettingInfo>
          <Toggle>
            <ToggleInput
              type="checkbox"
              checked={preferences.share_location}
              onChange={(e) => updatePreference('share_location', e.target.checked)}
            />
            <ToggleSlider />
          </Toggle>
        </SettingRow>

        <SettingRow>
          <SettingInfo>
            <SettingLabel>Auto-Share Active Vessel</SettingLabel>
            <SettingDescription>
              Automatically share location when your active vessel is moving
            </SettingDescription>
          </SettingInfo>
          <Toggle>
            <ToggleInput
              type="checkbox"
              checked={preferences.auto_share_active_vessel}
              onChange={(e) => updatePreference('auto_share_active_vessel', e.target.checked)}
              disabled={!preferences.share_location}
            />
            <ToggleSlider />
          </Toggle>
        </SettingRow>
      </Card>

      <Card>
        <CardTitle>
          <BellIcon className="w-5 h-5" />
          Proximity Notifications
        </CardTitle>
        
        <SettingRow>
          <SettingInfo>
            <SettingLabel>Enable Proximity Notifications</SettingLabel>
            <SettingDescription>
              Get notified when friends are nearby
            </SettingDescription>
          </SettingInfo>
          <Toggle>
            <ToggleInput
              type="checkbox"
              checked={preferences.proximity_notifications}
              onChange={(e) => updatePreference('proximity_notifications', e.target.checked)}
            />
            <ToggleSlider />
          </Toggle>
        </SettingRow>

        {preferences.proximity_notifications && (
          <SettingRow>
            <SettingInfo>
              <SettingLabel>Notification Radius</SettingLabel>
              <SettingDescription>
                Distance in nautical miles to trigger proximity notifications
              </SettingDescription>
            </SettingInfo>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Input
                type="number"
                min="0.1"
                max="50"
                step="0.1"
                value={preferences.proximity_radius_nm}
                onChange={(e) => updatePreference('proximity_radius_nm', parseFloat(e.target.value))}
              />
              <span style={{ color: theme.colors.text.secondary }}>nm</span>
            </div>
          </SettingRow>
        )}
      </Card>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
        <Button onClick={savePreferences} disabled={saving}>
          <CheckIcon className="w-5 h-5" />
          {saving ? 'Saving...' : 'Save Preferences'}
        </Button>
      </div>
    </Container>
  );
};

export default LocationPreferences;

